import { z } from 'zod';

import { UuidSchema } from '../../common/id/id';

/**
 * Moving **one** question to a new position, stated as the neighbour it should
 * follow.
 *
 * This is the counterpart to {@link ReorderItemsSchema}, not a replacement for
 * it, and the difference is what the client can see. A whole-set reorder is the
 * right shape when the client holds the whole set — the sections rail and an
 * options list both do. A **paged, filtered** question pane never does: it holds
 * twenty of five hundred rows, chosen by `search` and `isActive`, so it cannot
 * send the full order the reorder endpoint demands (and today's builder loads a
 * hardcoded hundred rows precisely to avoid admitting that).
 *
 * `after` is an **anchor, never an index**. An index is only meaningful against
 * an unfiltered, unpaged list; translating a page-relative one into an absolute
 * position requires exactly the knowledge paging withholds. The id of the row
 * the question was dropped after is knowledge the pane always has, and it stays
 * correct however the pane is filtered. `null` means "first in the section" —
 * omitting it is refused rather than read as first, because a body that forgot
 * the field would otherwise silently move a question to the top.
 *
 * The server still assigns every `sortOrder` from positions it computed, so gaps
 * and collisions remain impossible by construction — a client never sends a
 * position.
 */
export const MoveQuestionSchema = z
  .object({
    /** The section it should end up in. Omitted = stay where it is. */
    sectionId: UuidSchema.optional(),
    /** The question it should sit immediately after, or `null` for first. */
    after: UuidSchema.nullable(),
  })
  .strict();

export type MoveQuestionRequest = z.infer<typeof MoveQuestionSchema>;
